import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Container, media, Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {PayloadImage} from 'UI/components/layout/PayloadImage';
import {H3, Label} from 'UI/components/layout/Text';
import {ViewportTrigger} from 'UI/components/layout/ViewportTrigger';

import {FadeInAnimation} from 'UI/animations/shared';

import {useQuote} from './useQuote';

import type {QuoteType} from 'types/payload-types';

/**
 * The props for the `Quote` component, which renders a highlighted quotation with optional author and image.
 * This interface defines the structure of the data required to render the component, including
 * the quote text, the attributed person's name, and an optional image. A `testId` is also supported
 * to enable reliable end-to-end testing.
 */
interface ComponentProps {
    /**
     * An optional flag to hide the entire block when set to true.
     * When `true`, the component renders nothing.
     *
     * @default false
     */
    hideBlock?: QuoteType['hideBlock'];
    /**
     * The optional image associated with the person attributed to the quote.
     * This can be used to display a portrait or logo below the text.
     */
    image: QuoteType['image'];
    /**
     * The name of the person attributed to the quote.
     * If provided, it is displayed below the quoted text.
     */
    personName: QuoteType['personName'];
    /**
     * The main quote text to be displayed.
     * This is the primary content of the component and is wrapped in quotation marks.
     */
    quote: QuoteType['quote'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Quote'
     */
    testId?: string;
}

/**
 * A component that displays a stylized quote with optional author and image.
 * The `Quote` component renders a blockquote-like section containing the quoted text,
 * the name of the person attributed to the quote, and an optional image. Text and label
 * colors are dynamically provided by the `useQuote` hook to ensure proper contrast
 * with the background. The quote text is wrapped in quotation marks for clarity.
 * This component also provides spacing between elements to maintain a clean layout
 * and applies a `data-cy` attribute for reliable targeting in end-to-end tests.
 *
 * @param props            The component props.
 * @param props.hideBlock  An optional flag to hide the entire block when set to true. Defaults to `false`.
 * @param props.image      Optional image associated with the quoted person, rendered below the text.
 * @param props.personName Optional name of the person attributed to the quote, rendered as a label.
 * @param props.quote      The quote text itself, displayed prominently at the top of the section.
 * @param props.testId     Optional test ID used for automation selectors. Defaults to `'Quote'`.
 * @returns A JSX element rendering the quote section with text, author, and image.
 *
 * @example
 * ```tsx
 * <Quote
 *   quote="The only limit to our realization of tomorrow is our doubts of today."
 *   personName="Franklin D. Roosevelt"
 *   image={authorImage}
 * />
 * ```
 */
const Quote = observer(({hideBlock = false, image, personName, quote, testId = 'Quote'}: ComponentProps) => {
    const {colors} = useQuote();

    return hideBlock ? null : (
        <Container hasNoPadding={['xs', 'sm']} testId={testId} isFluid>
            <QuoteWrapper>
                <ViewportTrigger baseComponent={TextWrapper}>
                    {quote && (
                        <MotionH3 $align="center" as="p" variants={FadeInAnimation}>
                            <Color $color={colors.textPrimaryInverted}>„{quote}”</Color>
                        </MotionH3>
                    )}
                    <Spacer y={10} />
                    {personName && (
                        <StyledLabel $align="center" variants={FadeInAnimation}>
                            <Color $color={colors.textSecondaryInverted}>{personName}</Color>
                        </StyledLabel>
                    )}
                    <Spacer y={6} />
                    {image && (
                        <motion.div variants={FadeInAnimation}>
                            <StyledPayloadImage image={image} />
                        </motion.div>
                    )}
                </ViewportTrigger>
            </QuoteWrapper>
        </Container>
    );
});

Quote.displayName = 'Quote';

export {Quote};

const QuoteWrapper = styled.div`
    background-color: ${({theme}) => theme.colors.neutral800};
    display: flex;
    justify-content: center;
    padding-block: ${spacing(24)};
    padding-inline: ${spacing(6)};

    ${media('md')} {
        border-radius: 24px;
        padding-block: ${spacing(40)};
    }
`;

const TextWrapper = styled.div`
    align-items: center;
    display: flex;
    flex-direction: column;
    max-width: 800px;
    text-align: center;
`;

const MotionH3 = styled(motion.create(H3))``;

const StyledLabel = styled(motion.create(Label))`
    max-width: 38.2rem;
`;

const StyledPayloadImage = styled(PayloadImage)`
    border-radius: 50%;
    height: 80px;
    object-fit: cover;
    width: 80px;
`;